import { useCallback, useEffect, useState } from 'react'
import { api, type OffboardingGate } from '../lib/api'
import type { ExitItemState } from '../lib/exit'
import { ErrorBox, Loading } from './DataTable'
import { ExitEvidencePanel } from './exit/ExitEvidencePanel'
import { TaskList } from './kit'

// Section 17's checklist, on a screen.
//
// The gate, the table, the eleven items and two routes have existed since
// migration 0033, and for a long time nothing in the console called either
// route: a person could be refused with OFFBOARDING_INCOMPLETE naming eleven
// things and have no way to record one. The checklist now lives on the exit
// workspace as its evidence panel (components/exit/ExitEvidencePanel.tsx),
// where every item opens a drawer with the API's own words and the form.
//
// Two things stay here. OffboardingChecklist is the list on its own, which the
// lifecycle control renders under a refused close so the reader sees exactly
// which items the refusal named. VendorOffboarding is the panel loaded on its
// own, for a screen that shows the checklist without the chain.

// The checklist as it stood when a close was refused: required items missing
// are marked, optional ones say "Not yet", recorded ones go quiet.
export function OffboardingChecklist({ gate }: { gate: OffboardingGate }) {
  return (
    <TaskList
      items={gate.items.map((plain) => {
        const i = plain as ExitItemState
        return {
          key: i.key,
          name: i.name,
          state: i.done ? 'done' : i.mandatory ? 'missing' : 'waiting',
          note: i.evidenceMissing ? 'Needs its evidence again' : undefined,
        }
      })}
    />
  )
}

export function VendorOffboarding({ vendorId, onRecorded }: { vendorId: string; onRecorded?: () => void }) {
  const [gate, setGate] = useState<OffboardingGate | null>(null)
  const [recordable, setRecordable] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [openKey, setOpenKey] = useState<string | null>(null)

  const load = useCallback(() => {
    setError(null)
    api
      .vendorOffboarding(vendorId)
      .then((v) => {
        setGate(v.offboarding)
        setRecordable(v.recordable)
      })
      .catch(setError)
  }, [vendorId])

  useEffect(load, [load])

  if (error) return <ErrorBox error={error} what="This checklist" />
  if (!gate) return <Loading what="the offboarding checklist" />

  return (
    <ExitEvidencePanel
      vendorId={vendorId}
      gate={gate}
      recordable={recordable}
      owners={{}}
      holds={null}
      openKey={openKey}
      onOpen={setOpenKey}
      onRecorded={(next) => {
        // The response carries the whole re-evaluated checklist, so the panel
        // never shows a list assembled from a stale read plus one changed row.
        setGate(next)
        onRecorded?.()
      }}
    />
  )
}
